import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/lib/supabase";
import { isSafeNext } from "@/lib/safe-next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useSignInWithOtp } from "./use-sign-in-with-otp";

type Mode = "partner" | "internal";

const partnerSchema = z.object({
  email: z.string().email("Enter a valid email."),
});
type PartnerForm = z.infer<typeof partnerSchema>;

const internalSchema = z.object({
  email: z.string().email("Enter a valid email."),
  password: z.string().min(1, "Enter your password."),
});
type InternalForm = z.infer<typeof internalSchema>;

// Replaces /app/(auth)/login/LoginForm.tsx. Two-mode form (partner magic link
// vs internal password) with `?next` / `?error` / `?sent=1` honored. An
// already-authenticated visitor is bounced to their role-based home — mirrors
// the legacy middleware redirect.
export function LoginPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const next = search.get("next");
  const initialError = search.get("error");
  const initialSent = search.get("sent") === "1";

  const [mode, setMode] = useState<Mode>("partner");
  const [sent, setSent] = useState(initialSent);
  const [pendingEmail, setPendingEmail] = useState("");

  // Surface ?error= as a toast on mount. Only fire once per URL state.
  useEffect(() => {
    if (initialError) toast.error(decodeURIComponent(initialError));
  }, [initialError]);

  // Authed user lands on /login — bounce to role home (or honored ?next=).
  useEffect(() => {
    if (loading || !session) return;
    const fallback = session.role === "internal" ? "/admin/dashboard" : "/portal/dashboard";
    const target = isSafeNext(next) ? next : fallback;
    navigate(target, { replace: true });
  }, [session, loading, next, navigate]);

  const otp = useSignInWithOtp();

  const partnerForm = useForm<PartnerForm>({
    resolver: zodResolver(partnerSchema),
    defaultValues: { email: "" },
  });
  const internalForm = useForm<InternalForm>({
    resolver: zodResolver(internalSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onPartnerSubmit(values: PartnerForm) {
    try {
      await otp.mutateAsync({ email: values.email, next });
      setPendingEmail(values.email);
      setSent(true);
    } catch {
      // useSignInWithOtp already toasts on error.
    }
  }

  async function onInternalSubmit(values: InternalForm) {
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Welcome back.");
    const target = isSafeNext(next) ? next : "/admin/dashboard";
    navigate(target, { replace: true });
  }

  if (sent && mode === "partner") {
    return (
      <div className="rounded-xl border bg-card p-8 shadow-sm">
        <Alert className="border-accent/30 bg-accent/5">
          <Mail className="h-4 w-4 text-accent" />
          <AlertDescription>
            <div className="font-medium text-foreground">Check your email</div>
            <p className="mt-1 text-muted-foreground">
              We sent a magic link to{" "}
              <span className="font-medium text-foreground">{pendingEmail}</span>. It expires in
              1 hour.
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setPendingEmail("");
                partnerForm.reset();
              }}
              className="mt-3 text-xs font-medium text-accent underline-offset-4 hover:underline"
            >
              Use a different email
            </button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-8 shadow-sm">
      <div className="mb-6 space-y-1.5">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Sign in to Colab</h1>
        <p className="text-sm text-muted-foreground">
          Partners get a magic link. Bookboost team uses email and password.
        </p>
      </div>

      <div className="mb-4 inline-flex w-full rounded-md border bg-muted/40 p-1 text-xs">
        <button
          type="button"
          onClick={() => setMode("partner")}
          className={`flex-1 rounded px-3 py-1.5 font-medium transition ${
            mode === "partner"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Partner
        </button>
        <button
          type="button"
          onClick={() => setMode("internal")}
          className={`flex-1 rounded px-3 py-1.5 font-medium transition ${
            mode === "internal"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Bookboost team
        </button>
      </div>

      {mode === "partner" ? (
        <Form {...partnerForm}>
          <form onSubmit={partnerForm.handleSubmit(onPartnerSubmit)} className="space-y-4">
            <FormField
              control={partnerForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@partner.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={otp.isPending} className="w-full">
              {otp.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send magic link
            </Button>
          </form>
        </Form>
      ) : (
        <form onSubmit={internalForm.handleSubmit(onInternalSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="internal-email">Email</Label>
            <Input
              id="internal-email"
              type="email"
              autoComplete="username"
              placeholder="you@bookboost.io"
              aria-invalid={!!internalForm.formState.errors.email}
              {...internalForm.register("email")}
            />
            {internalForm.formState.errors.email && (
              <p className="text-[0.8rem] font-medium text-destructive">
                {internalForm.formState.errors.email.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="internal-password">Password</Label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="internal-password"
              type="password"
              autoComplete="current-password"
              aria-invalid={!!internalForm.formState.errors.password}
              {...internalForm.register("password")}
            />
            {internalForm.formState.errors.password && (
              <p className="text-[0.8rem] font-medium text-destructive">
                {internalForm.formState.errors.password.message}
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={internalForm.formState.isSubmitting}
            className="w-full"
          >
            {internalForm.formState.isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Sign in
          </Button>
        </form>
      )}

      <p className="mt-6 border-t pt-4 text-center text-xs text-muted-foreground">
        Need an invite?{" "}
        <a
          href="mailto:partnerships@bookboost.io?subject=Colab%20access%20request"
          className="font-medium text-foreground underline-offset-4 hover:underline"
        >
          Email the partnerships team
        </a>
      </p>
    </div>
  );
}
